/**
 * Clears all session data from localStorage, sessionStorage, and cookies.
 * Called during logout to ensure a fresh session on the next login.
 */

// Shared with clearStaleSearchFilters() below - kept as its own list so a
// fresh login can wipe just these without touching the auth/session keys
// cleanSession() also clears (which would be wrong to touch right after a
// token was just issued).
const SEARCH_FILTER_STORAGE_KEYS = [
  "peopleSearchFilters_allTab",
  "peopleSearchFilters_savedTab",
  "companySearchFilters",
  "companySavedCompanyFilters",
];

export const cleanSession = () => {
  if (typeof window === "undefined") return;

  // Keys to remove from localStorage
  const localStorageKeysToRemove = [
    "token",
    "personNameSuppressionLists",
    ...SEARCH_FILTER_STORAGE_KEYS,
  ];

  // Remove localStorage keys
  localStorageKeysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing to do
    }
  });

  // The sign-out reason (SIGN_OUT_REASON_KEY) is deliberately left in place.
  // The axios interceptor writes it just before the session-expired path runs
  // this function, and the login page is what reads and removes it — clearing
  // it here wiped the message before it could ever be shown.

  // Clear cookies
  clearCookies();
};

/**
 * Wipes any leftover search-filter localStorage from a previous session.
 *
 * cleanSession() already removes these on every logout path, but a session
 * can also end without one running at all - a crashed tab, a force-quit
 * browser, or a session revoked server-side while this tab was closed - so a
 * stale entry can still be sitting in localStorage the next time someone
 * logs in. usePeopleActions/useCompaniesActions restore filters from these
 * keys on mount, so a leftover entry silently reapplies a previous session's
 * (or previous user's, on a shared machine) filters - see BUG_077. Called
 * right after a successful login/register, before any filter UI mounts.
 */
export const clearStaleSearchFilters = () => {
  if (typeof window === "undefined") return;
  SEARCH_FILTER_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing to do
    }
  });
};

/**
 * Clears authentication-related cookies.
 * These are the cookies set by the backend during login.
 */
function clearCookies() {
  if (typeof window === "undefined") return;

  const cookiesToClear = ["session", "refresh_token"];

  cookiesToClear.forEach((name) => {
    try {
      // Clear cookie by setting max-age to 0
      document.cookie = `${name}=; path=/; max-age=0`;
      // Also try to clear with a domain (if the cookie was set with one)
      document.cookie = `${name}=; path=/; max-age=0; domain=${window.location.hostname}`;
    } catch {
      // Cookie operation failed — continue
    }
  });
}
